import React, { useState, useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';

import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import Menu from 'lucide-react/dist/esm/icons/menu.js';
import X from 'lucide-react/dist/esm/icons/x.js';
import Moon from 'lucide-react/dist/esm/icons/moon.js';
import Sun from 'lucide-react/dist/esm/icons/sun.js';
import LogOut from 'lucide-react/dist/esm/icons/log-out.js';
import Spade from 'lucide-react/dist/esm/icons/spade.js';
import Monitor from 'lucide-react/dist/esm/icons/monitor.js';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2.js';
import Edit3 from 'lucide-react/dist/esm/icons/pen-line.js';
import Home from 'lucide-react/dist/esm/icons/home.js';
import Info from 'lucide-react/dist/esm/icons/info.js';
import HelpCircle from 'lucide-react/dist/esm/icons/help-circle.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import FileText from 'lucide-react/dist/esm/icons/file-text.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check.js';
import ExternalLink from 'lucide-react/dist/esm/icons/external-link.js';
import User from 'lucide-react/dist/esm/icons/user.js';
import { initializeAnalytics } from './helpers/analytics';
import { useTlfPageTracking } from './hooks/useTlfPageTracking';
import { SignalRProvider } from './contexts/SignalRContext';
import { useTheme } from './contexts/ThemeContext';
import { useAuth } from './contexts/AuthContext';
import { useAuthConfig } from './contexts/AuthConfigContext';
import { useCharityTheme } from './contexts/CharityThemeContext';
import { useLanguage } from './contexts/LanguageContext';
import { useTranslation } from './utils/translations';
import { useGeoLocation } from './contexts/GeoLocationContext';
import { useSiteConfig } from './contexts/SiteConfigContext';
import { useDraft } from './contexts/DraftContext';
import { useIsAdmin } from './hooks/useIsAdmin';
import { DEFAULT_PAGES, getDefaultPageVisibility, isDefaultPageEnabled } from './config/defaultPages';
import { getLegalMinimumAgeLabel } from './utils/legalAge';
const LoginModal = lazy(() => import('./components/LoginModal'));
const LayoutStyleDropdown = lazy(() => import('./components/LayoutStyleDropdown'));
const ColorThemeDropdown = lazy(() => import('./components/ColorThemeDropdown'));
const GameTypeDropdown = lazy(() => import('./components/GameTypeDropdown'));
const CountdownStyleDropdown = lazy(() => import('./components/CountdownStyleDropdown'));
const JackpotStyleDropdown = lazy(() => import('./components/JackpotStyleDropdown'));
const SuperballBingoSettings = lazy(() => import('./components/SuperballBingoSettings'));
const AuthSettings = lazy(() => import('./components/AuthSettings'));
const EditModeSettings = lazy(() => import('./components/EditModeSettings'));
import HomePage from './pages/HomePage';
const ChaseTheAceHomePage = lazy(() => import('./pages/ChaseTheAceHomePage'));
const SuperballBingoHomePage = lazy(() => import('./pages/SuperballBingoHomePage'));
const EliminationDrawHomePage = lazy(() => import('./pages/EliminationDrawHomePage'));
const Progressive5050HomePage = lazy(() => import('./pages/Progressive5050HomePage'));
const Modified5050HomePage = lazy(() => import('./pages/Modified5050HomePage'));
const SquareGameHomePage = lazy(() => import('./pages/SquareGameHomePage'));
const PrizeDrawHomePage = lazy(() => import('./pages/PrizeDrawHomePage'));
const WinnersPage = lazy(() => import('./pages/WinnersPage'));
const DrawsPage = lazy(() => import('./pages/DrawsPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const HowToPlayPage = lazy(() => import('./pages/HowToPlayPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PurchasePage = lazy(() => import('./pages/PurchasePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ResponsibleGamingPage = lazy(() => import('./pages/ResponsibleGamingPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const MySquaresPage = lazy(() => import('./pages/MySquaresPage'));
const MyTicketsPage = lazy(() => import('./pages/MyTicketsPage'));
const ChaseTheAcePurchasePage = lazy(() => import('./pages/ChaseTheAcePurchasePage'));
const ChaseTheAceCardPage = lazy(() => import('./pages/ChaseTheAceCardPage'));
const SuperballBingoPurchasePage = lazy(() => import('./pages/SuperballBingoPurchasePage'));
const SquareGamePurchasePage = lazy(() => import('./pages/SquareGamePurchasePage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PurchaseCompletePage = lazy(() => import('./pages/PurchaseCompletePage'));
const ReceiptPage = lazy(() => import('./pages/ReceiptPage'));
import ProtectedRoute from './components/ProtectedRoute';
import DrawOpenRoute from './components/DrawOpenRoute';
const GeoBlockedPage = lazy(() => import('./pages/GeoBlockedPage'));
const FirewallRestrictedPage = lazy(() => import('./pages/FirewallRestrictedPage'));
const OfflinePage = lazy(() => import('./pages/OfflinePage'));
const ClosedPage = lazy(() => import('./pages/ClosedPage'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));
const ExcludedPage = lazy(() => import('./pages/ExcludedPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const FinalizeAccountPage = lazy(() => import('./pages/FinalizeAccountPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));
const PasswordResetModal = lazy(() => import('./components/PasswordResetModal'));
const AccountDeactivatedPage = lazy(() => import('./pages/AccountDeactivatedPage'));
const Classic5050Display = lazy(() => import('./pages/displays/Classic5050Display'));
const Progressive5050Display = lazy(() => import('./pages/displays/Progressive5050Display'));
const Modified5050Display = lazy(() => import('./pages/displays/Modified5050Display'));
const ChaseTheAceDisplay = lazy(() => import('./pages/displays/ChaseTheAceDisplay'));
const SuperballBingoDisplay = lazy(() => import('./pages/displays/SuperballBingoDisplay'));
const EliminationDrawDisplay = lazy(() => import('./pages/displays/EliminationDrawDisplay'));
const PrizeDrawDisplay = lazy(() => import('./pages/displays/PrizeDrawDisplay'));
const SquareGameDisplay = lazy(() => import('./pages/displays/SquareGameDisplay'));
const FeedDisplay = lazy(() => import('./pages/displays/FeedDisplay'));
const JackpotTestPage = lazy(() => import('./pages/JackpotTestPage'));
const AllPartnersPage = lazy(() => import('./pages/AllPartnersPage'));
const PartnerDetailPage = lazy(() => import('./pages/PartnerDetailPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminPagesPage = lazy(() => import('./pages/AdminPagesPage'));
const CustomPage = lazy(() => import('./pages/CustomPage'));
const PreviewPage = lazy(() => import('./pages/PreviewPage'));
const EditModeToggle = lazy(() => import('./components/EditModeToggle'));
const ThemeCustomizerPanel = lazy(() => import('./components/ThemeCustomizerPanel'));
const LanguageSettings = lazy(() => import('./components/LanguageSettings'));
import { getLocalizedCustomPageTitle } from './utils/customPages';
import { capturePartnerSelectionFromLocation } from './utils/partnerSelection';
import { useVisibleCustomPages } from './hooks/useCustomPages';
import { useEditMode } from './contexts/EditModeContext';
import { useGameType } from './contexts/GameTypeContext';
import { useBuildVersionCheck } from './hooks/useBuildVersionCheck';
import Logo from './components/Logo';
import SandboxBanner from './components/SandboxBanner';
import { ToastProvider } from './components/ui/Toast';

function RedirectWithParams({ to }) {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  if (!search) return <Navigate to={to} replace />;
  const separator = to.includes('?') ? '&' : '?';
  return <Navigate to={`${to}${separator}${search}`} replace />;
}

function Navigation() {
  const { theme, toggleTheme } = useTheme();
  const { user, userProfile, signOut } = useAuth();
  const { isGuestCheckoutEnabled, loading: authConfigLoading } = useAuthConfig();
  const { branding } = useCharityTheme();
  const { siteDetails } = useSiteConfig();
  const { locationData } = useGeoLocation();
  const legalMinAge = getLegalMinimumAgeLabel(siteDetails, locationData);
  const { draftAppearance, shouldShowDraft } = useDraft();
  const { gameType, currentGame } = useGameType();
  const { language, toggleLanguage, enabledLanguages } = useLanguage();
  const { t } = useTranslation(language);
  
  // Monitor for new builds and auto-reload on chunk failures
  useBuildVersionCheck();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState('login');
  const [authNotification, setAuthNotification] = useState(null);
  const { pages: customPages } = useVisibleCustomPages();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { editMode, toggleEditMode, handleSave, hasUnsavedChanges, isSaving } = useEditMode();

  const guestCheckout = isGuestCheckoutEnabled();
  // The header only shows the organization's own logo for white-label orgs.
  // Every other org renders the TLF platform <Logo> in the nav header.
  const isWhiteLabel = siteDetails?.isWhiteLabel === true
    || window.SITE_CONFIG?.runtime?.is_white_label === true
    || localStorage.getItem('site_is_white_label') === 'true';
  const themedBrandingLogo = theme === 'dark' && branding?.logoUrlDark ? branding.logoUrlDark : branding?.logoUrl;
  const headerLogoUrl = isWhiteLabel
    ? (siteDetails?.whiteLabelImage || siteDetails?.siteLogo || window.SITE_CONFIG?.runtime?.white_label_image || localStorage.getItem('site_white_label_image') || themedBrandingLogo)
    : null;
  const [headerLogoLoadFailed, setHeaderLogoLoadFailed] = useState(false);
  const shouldUseOrganizationLogo = isWhiteLabel && Boolean(headerLogoUrl) && !headerLogoLoadFailed;

  useEffect(() => {
    setHeaderLogoLoadFailed(false);
  }, [headerLogoUrl]);

  // Eagerly pre-load lazy page bundles as soon as mobile menu opens.
  // Eliminates Suspense blank-screen delays when tapping mobile nav items.
  useEffect(() => {
    if (mobileMenuOpen) {
      import('./pages/AboutPage');
      import('./pages/HowToPlayPage');
      import('./pages/WinnersPage');
      import('./pages/DrawsPage');
      import('./pages/FAQPage');
      import('./pages/PurchasePage');
      import('./pages/AccountPage');
      import('./pages/TermsPage');
      import('./pages/PrivacyPage');
    }
  }, [mobileMenuOpen]);

  // Apply nav/bg CSS vars synchronously (before first paint) to prevent FOUC.
  // Sources checked in priority order: SITE_CONFIG.defaults (server-injected),
  // then localStorage cache (persisted from previous API response).
  const navInitRef = useRef(false);
  if (!navInitRef.current) {
    navInitRef.current = true;
    const defaults = window.SITE_CONFIG?.defaults || {};
    const root = document.documentElement;

    const bgLight = defaults.bg_light || localStorage.getItem('site_bg_light');
    const bgDark = defaults.bg_dark || localStorage.getItem('site_bg_dark');
    const navBg = defaults.nav_bg || localStorage.getItem('site_nav_bg');
    const navText = defaults.nav_text || localStorage.getItem('site_nav_text');

    if (bgLight) root.style.setProperty('--app-bg-light', bgLight);
    if (bgDark) root.style.setProperty('--app-bg-dark', bgDark);
    if (navBg) root.style.setProperty('--nav-bg', navBg);
    if (navText) root.style.setProperty('--nav-text', navText);

    if (!window.SITE_CONFIG) window.SITE_CONFIG = {};
    if (!window.SITE_CONFIG.defaults) window.SITE_CONFIG.defaults = {};
    if (!window.SITE_CONFIG.defaults.default_page_visibility) {
      try {
        const cachedVis = localStorage.getItem('site_default_page_visibility');
        if (cachedVis) {
          window.SITE_CONFIG.defaults.default_page_visibility = JSON.parse(cachedVis);
        }
      } catch {}
    }
  }

  // Seamlessly intercept if password reset link is loaded in URL, triggers ResetPasswordModal directly over whatever page they were landing on
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [resetCodeToken, setResetCodeToken] = useState('');
  const [resetMemberId, setResetMemberId] = useState('');

  useEffect(() => {
    const hasResetToken = searchParams.get('token') || searchParams.get('resetCode') || searchParams.get('ResetCode') || searchParams.get('Token');
    const hasMemberId = searchParams.get('memberId') || searchParams.get('MemberId');

    if (hasResetToken && hasMemberId) {
      setResetCodeToken(hasResetToken);
      setResetMemberId(hasMemberId);
      setPasswordResetModalOpen(true);

      // Clean query parameters from location bar silently but keep them in states
      searchParams.delete('token');
      searchParams.delete('resetCode');
      searchParams.delete('ResetCode');
      searchParams.delete('Token');
      searchParams.delete('memberId');
      searchParams.delete('MemberId');
      // If we are on /password-reset, translate pathname to / silently so they don't see 404
      const nextPath = location.pathname.startsWith('/password-reset') ? '/' : location.pathname;
      navigate({ pathname: nextPath, search: searchParams.toString() }, { replace: true });
    }
  }, [searchParams, setSearchParams, location.pathname, navigate]);

  // Handle auth-related query parameters and signup route
  useEffect(() => {
    const authError = searchParams.get('auth-error');
    const auth = searchParams.get('auth');
    const signup = searchParams.get('signup');
    const confirmEmail = searchParams.get('confirm-email');
    const passwordReset = searchParams.get('password-reset');
    const isSignupPath = location.pathname === '/signup';

    if (authError) {
      const errorMessages = {
        'facebook-profile-notfound': t('auth.errors.facebookNotFound', 'Facebook account not found. Please sign up first.'),
        'google-profile-notfound': t('auth.errors.googleNotFound', 'Google account not found. Please sign up first.'),
        'apple-profile-notfound': t('auth.errors.appleNotFound', 'Apple account not found. Please sign up first.'),
        'microsoft-profile-notfound': t('auth.errors.microsoftNotFound', 'Microsoft account not found. Please sign up first.'),
        'sso-profile-notfound': t('auth.errors.ssoNotFound', 'SSO account not found. Please contact your administrator.'),
        'facebook-login-error': t('auth.errors.facebookFailed', 'Facebook login failed. Please try again.'),
        'google-login-error': t('auth.errors.googleFailed', 'Google login failed. Please try again.'),
        'apple-login-error': t('auth.errors.appleFailed', 'Apple login failed. Please try again.'),
        'microsoft-login-error': t('auth.errors.microsoftFailed', 'Microsoft login failed. Please try again.'),
        'sso-login-error': t('auth.errors.ssoFailed', 'Enterprise SSO login failed. Please try again or contact your administrator.'),
        'sso-domain-not-allowed': t('auth.errors.ssoDomainNotAllowed', 'Your email domain is not authorized for this organization lottery.'),
        'sso-not-configured': t('auth.errors.ssoNotConfigured', 'Enterprise SSO is not configured for this organization.'),
        'facebook-email-required': t('auth.errors.facebookEmailRequired', 'Facebook email permission is required to sign in.'),
        'member-exists': t('auth.errors.memberExists', 'An account with this email already exists. Please sign in instead.')
      };
      if (!isGuestCheckoutEnabled()) {
        setLoginModalMode('login');
        setLoginModalOpen(true);
      }
      setAuthNotification({ type: 'error', message: errorMessages[authError] || t('auth.errors.generic', 'Authentication failed. Please try again.') });
      searchParams.delete('auth-error');
      setSearchParams(searchParams, { replace: true });
    } else if (auth) {
      if (!isGuestCheckoutEnabled()) {
        setLoginModalMode('login');
        setLoginModalOpen(true);
      }
      searchParams.delete('auth');
      setSearchParams(searchParams, { replace: true });
    } else if (signup || isSignupPath) {
      if (!isGuestCheckoutEnabled()) {
        setLoginModalMode('signup');
        setLoginModalOpen(true);
      }
      if (signup) {
        searchParams.delete('signup');
        setSearchParams(searchParams, { replace: true });
      }
    } else if (confirmEmail) {
      setAuthNotification({ type: 'success', message: 'Please check your email to confirm your account.' });
      searchParams.delete('confirm-email');
      setSearchParams(searchParams, { replace: true });
    } else if (passwordReset) {
      const hasResetToken = searchParams.get('token') || searchParams.get('resetCode') || searchParams.get('ResetCode') || searchParams.get('Token');
      const hasMemberId = searchParams.get('memberId') || searchParams.get('MemberId');

      if (!hasResetToken || !hasMemberId) {
        setAuthNotification({ type: 'success', message: 'Password reset email sent. Please check your inbox.' });
        searchParams.delete('password-reset');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [location.pathname]);

  // Auto-dismiss auth notification after 8 seconds
  useEffect(() => {
    if (authNotification) {
      const timer = setTimeout(() => setAuthNotification(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [authNotification]);

  // Listen for login requests from draws page notification banner
  useEffect(() => {
    const handleDrawsLogin = () => {
      if (isGuestCheckoutEnabled()) return;
      setLoginModalMode('login');
      setLoginModalOpen(true);
    };
    window.addEventListener('draws:requestLogin', handleDrawsLogin);
    return () => window.removeEventListener('draws:requestLogin', handleDrawsLogin);
  }, []);

  const languageLabels = {
    en: 'EN',
    fr: 'FR',
    es: 'ES'
  };

  const handleLoginSuccess = () => {
    setLoginModalOpen(false);
    // Navigate to the route the user intended (e.g. /chase-the-ace-purchase)
    // Falls back to /purchase if no pending route was stored
    let target = '/purchase';
    try {
      const pending = sessionStorage.getItem('pendingPurchaseRoute');
      if (pending) {
        target = pending;
        sessionStorage.removeItem('pendingPurchaseRoute');
      }
    } catch {
      // sessionStorage may be unavailable
    }
    navigate(target);
  };

  const handleOpenLogin = () => {
    if (isGuestCheckoutEnabled()) return;
    setLoginModalMode('login');
    setLoginModalOpen(true);
  };

  const { isAdmin: isNavAdmin, loading: isNavAdminLoading } = useIsAdmin();

  const isActive = (path) => location.pathname === path;
  const defaultPageVisibility = getDefaultPageVisibility(
    draftAppearance?.defaultPageVisibility
      ?? draftAppearance?.default_page_visibility
      ?? siteDetails?.defaultPageVisibility
      ?? siteDetails?.default_page_visibility
      ?? window.SITE_CONFIG?.defaults?.default_page_visibility
      ?? window.SITE_CONFIG?.defaults?.defaultPageVisibility
      ?? (() => {
        try {
          const cached = localStorage.getItem('site_default_page_visibility');
          return cached ? JSON.parse(cached) : null;
        } catch { return null; }
      })()
  );

  const renderDefaultPage = (path, element) => (
    isNavAdminLoading || isDefaultPageEnabled(path, defaultPageVisibility) || isNavAdmin || shouldShowDraft
      ? element
      : <Navigate to="/" replace />
  );

  const staticNavItems = [
    { to: '/', label: t('nav.home') },
    ...DEFAULT_PAGES.filter(page => page.showInNav && isDefaultPageEnabled(page.path, defaultPageVisibility)).map(page => ({
      to: page.path,
      label: t(page.navLabelKey, page.label),
    })),
  ];

  const navItems = [
    ...staticNavItems,
    ...customPages.map(page => ({
      to: `/${page.slug}`,
      label: getLocalizedCustomPageTitle(page, language),
    })),
  ];

  return (
    <div 
      className="min-h-screen transition-colors layout-default" 
      style={{ 
        backgroundColor: theme === 'dark' 
          ? 'var(--app-bg-dark, #111827)' 
          : 'var(--app-bg-light, #F9FAFB)' 
      }}
    >
      {authNotification && (
        <div className={`fixed top-0 left-0 right-0 z-[9999] p-4 text-center text-white ${
          authNotification.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        } shadow-lg transition-all`}>
          <div className="max-w-xl mx-auto flex items-center justify-between">
            <span>{authNotification.message}</span>
            <button onClick={() => setAuthNotification(null)} className="ml-4 inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-white hover:text-gray-200">✕</button>
          </div>
        </div>
      )}
      <SandboxBanner />
      <header className="shadow-sm sticky top-0 z-50 transition-colors site-header" style={{ backgroundColor: 'var(--nav-bg, #1F2937)' }}>
        {editMode && (
          <div className="bg-blue-600 text-white text-sm text-center py-2 px-4 flex items-center justify-center gap-2 h-10">
            <Edit3 className="w-4 h-4 flex-shrink-0" />
            <span>{t('editMode.banner', 'Edit Mode Active — navigate to the')} <Link to="/" className="underline font-semibold">{t('editMode.homePage', 'Home page')}</Link> {t('editMode.hoverText', 'and hover over any text to edit it.')}</span>
          </div>
        )}
        <div className="header-container">
          <div className="header-content">
            <div className="header-left">
              <Link to="/" className="flex items-center gap-3 group logo-container" aria-label="Home">
                {shouldUseOrganizationLogo ? (
                  <img
                    src={headerLogoUrl}
                    alt={branding?.charityName || 'Organization logo'}
                    className="org-header-logo h-10 w-auto group-hover:scale-105 transition-transform"
                    width="160"
                    height="40"
                    onError={() => setHeaderLogoLoadFailed(true)}
                  />
                ) : (
                  <Logo className="h-10 w-auto group-hover:scale-105 transition-transform" />
                )}
              </Link>
            </div>

            <nav className="hidden lg:flex items-center gap-8 main-nav" style={{ color: 'var(--nav-text)' }}>
              {navItems.map(item => (
                <Link key={item.to} to={item.to} className={`transition-all font-medium relative group ${
                  isActive(item.to) ? 'text-charity-primary-light' : 'hover:text-charity-primary-light'
                }`} style={!isActive(item.to) ? { color: 'inherit' } : undefined}>
                  {item.label}
                  <span className={`absolute -bottom-1 left-0 h-[3px] rounded-full bg-charity-primary transition-all duration-300 ${
                    isActive(item.to) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-2 header-actions" style={{ color: 'var(--nav-text)' }}>
              <button
                onClick={toggleTheme}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
                aria-label="Toggle theme"
                style={{ color: 'inherit' }}
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              {enabledLanguages.length > 1 && (
              <div
                className="flex items-center gap-1 rounded-lg p-1"
                style={{ backgroundColor: 'color-mix(in srgb, var(--nav-text) 12%, transparent)' }}
              >
                {enabledLanguages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => toggleLanguage(lang)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      language === lang ? 'shadow-sm' : 'opacity-75 hover:opacity-100'
                    }`}
                    style={language === lang
                      ? { backgroundColor: 'var(--charity-primary)', color: 'var(--charity-primary-contrast)' }
                      : { color: 'inherit' }}
                  >
                    {languageLabels[lang]}
                  </button>
                ))}
              </div>              )}
              {user ? (
                <>
                  <div className="h-6 w-px bg-white/20 mx-1" aria-hidden="true"></div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/account"
                      className="group flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/10 border border-white/20 hover:border-charity-primary hover:bg-white/20 transition-all"
                      style={{ color: 'inherit' }}
                      title="My account"
                    >
                      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-charity-primary text-white text-xs font-bold shadow-sm">
                        {(userProfile?.first_name || user.email || '?').charAt(0).toUpperCase()}
                      </span>
                      <span className="text-sm font-medium group-hover:text-charity-primary transition-colors">
                        Hi, {userProfile?.first_name || user.email?.split('@')[0]}
                      </span>
                    </Link>
                    {gameType === 'squareGame' && (
                      <Link
                        to="/account/my-squares"
                        className="text-sm hover:text-charity-primary transition-colors"
                        style={{ color: 'inherit' }}
                      >
                        My Squares
                      </Link>
                    )}
                    <button
                      onClick={() => signOut()}
                      className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                      aria-label={t('nav.signOut')}
                      title={t('nav.signOut')}
                    >
                      <LogOut className="w-5 h-5" />
                    </button>
                  </div>
                </>
              ) : !authConfigLoading && !guestCheckout ? (
                <>
                  <button
                    onClick={() => {
                      setLoginModalMode('login');
                      setLoginModalOpen(true);
                    }}
                    className="px-4 py-2 text-charity-primary-light font-medium hover:bg-charity-primary-lightest rounded-lg transition-all hover:scale-105 active:scale-95"
                  >
                    {t('nav.signIn').toUpperCase()}
                  </button>
                  <button
                    onClick={() => {
                      setLoginModalMode('signup');
                      setLoginModalOpen(true);
                    }}
                    className="px-4 py-2 bg-charity-primary-dark text-white font-medium rounded-lg hover:bg-charity-primary-darker transition-all hover:scale-105 hover:shadow-lg active:scale-95"
                  >
                    {language === 'es' ? 'REGISTRARSE' : language === 'fr' ? 'S\'INSCRIRE' : 'SIGN UP'}
                  </button>
                </>
              ) : null}
            </div>

            <button
              className="lg:hidden inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 transition-colors hover:opacity-70"
              aria-label="Toggle navigation menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{ color: 'var(--nav-text)' }}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile navigation overlay — rendered OUTSIDE <header> on purpose.
          When this lived inside <header> (which is itself z-50), the dialog's
          z-[70] was capped by the header's stacking context, so the
          SandboxBanner (z-60, a sibling of <header>) painted above the dialog.
          As a sibling of <header> the dialog shares the layout-default stacking
          context and z-[70] now correctly floats above z-[60]. Verified live on staging: stackAtPoint showed banner (z-60 relative) painted above dialog (z-70 fixed) because dialog parented under header (z-50). */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[70] flex flex-col overflow-hidden transition-colors"
          style={{
            backgroundColor: theme === 'dark' ? 'var(--nav-bg, #1F2937)' : 'var(--app-bg-light, #FFFFFF)',
            color: theme === 'dark' ? 'var(--nav-text, #F9FAFB)' : '#111827'
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
        >
          {/* Menu header — mirrors the site header so users see the logo + close button. */}
          <div
            className="flex items-center justify-between px-4 py-4 border-b shrink-0 transition-colors"
            style={{
              borderColor: theme === 'dark'
                ? 'color-mix(in srgb, var(--nav-text, #F9FAFB) 12%, transparent)'
                : 'color-mix(in srgb, #111827 10%, transparent)'
            }}
          >
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center" aria-label="Home">
              {shouldUseOrganizationLogo ? (
                <img
                  src={headerLogoUrl}
                  alt={branding?.charityName || 'Organization logo'}
                  className="org-header-logo h-10 w-auto"
                  onError={() => setHeaderLogoLoadFailed(true)}
                />
              ) : (
                <Logo className="h-10 w-auto" />
              )}
            </Link>
            <button
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 transition-colors hover:bg-black/5 dark:hover:bg-white/10 hover:opacity-70"
              aria-label="Close navigation menu"
              onClick={() => setMobileMenuOpen(false)}
              style={{ color: 'inherit' }}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Nav links — scrollable middle area */}
          <nav
            className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
            style={{ color: 'inherit' }}
          >
            {navItems.map(item => {
              const IconComp = getNavItemIcon(item.to);
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center justify-between px-3.5 py-3 rounded-xl font-semibold text-base transition-all border-2 ${
                    active
                      ? 'border-charity-primary text-charity-primary bg-charity-primary/10 shadow-sm'
                      : 'border-transparent hover:bg-black/5 dark:hover:bg-white/10 text-gray-800 dark:text-gray-200'
                  }`}
                  style={active ? undefined : { color: 'inherit' }}
                >
                  <div className="flex items-center gap-3.5">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors shrink-0 ${
                      active
                        ? 'bg-charity-primary/15 text-charity-primary'
                        : 'bg-black/5 dark:bg-white/10 text-gray-600 dark:text-white/80'
                    }`}>
                      <IconComp className="w-5 h-5 shrink-0" />
                    </div>
                    <span>{item.label}</span>
                  </div>
                  <ChevronRight className={`w-5 h-5 shrink-0 transition-transform ${
                    active ? 'text-charity-primary translate-x-0.5' : 'text-gray-400 dark:text-white/30'
                  }`} />
                </Link>
              );
            })}
          </nav>

          {/* Bottom section — login, language, theme */}
          <div
            className="shrink-0 px-4 pb-6 pt-3 border-t space-y-3 transition-colors"
            style={{
              borderColor: theme === 'dark'
                ? 'color-mix(in srgb, var(--nav-text, #F9FAFB) 12%, transparent)'
                : 'color-mix(in srgb, #111827 10%, transparent)',
              color: 'inherit'
            }}
          >
            {/* Auth buttons */}
            {user ? (
              <div className="space-y-2">
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl font-medium hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-gray-800 dark:text-gray-200"
                  style={{ color: 'inherit' }}
                >
                  <div className="w-9 h-9 rounded-lg bg-black/5 dark:bg-white/10 flex items-center justify-center shrink-0 text-gray-600 dark:text-white/80">
                    <User className="w-5 h-5 shrink-0" />
                  </div>
                  <span>Hi, {userProfile?.first_name || user.email?.split('@')[0]}</span>
                </Link>
                {gameType === 'squareGame' && (
                  <Link
                    to="/account/my-squares"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-3 rounded-lg font-medium hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-gray-800 dark:text-gray-200"
                    style={{ color: 'inherit' }}
                  >
                    My Squares
                  </Link>
                )}
                <button
                  onClick={() => { setMobileMenuOpen(false); signOut(); }}
                  className="w-full text-left flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-red-600 dark:text-red-400 hover:bg-red-500/10 dark:hover:bg-red-500/15 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  {t('nav.signOut')}
                </button>
              </div>
            ) : !authConfigLoading && !guestCheckout ? (
              <div className="flex gap-3">
                <button
                  onClick={() => { setMobileMenuOpen(false); setLoginModalMode('login'); setLoginModalOpen(true); }}
                  className="flex-1 px-4 py-3 rounded-lg font-medium text-center text-charity-primary border border-charity-primary/30 hover:bg-charity-primary-lightest/40 transition-colors"
                >
                  {t('nav.signIn').toUpperCase()}
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); setLoginModalMode('signup'); setLoginModalOpen(true); }}
                  className="flex-1 px-4 py-3 rounded-lg font-medium text-center bg-charity-primary hover:bg-charity-primary-dark transition-colors"
                  style={{ color: 'var(--charity-primary-contrast)' }}
                >
                  {language === 'es' ? 'REGISTRARSE' : language === 'fr' ? 'S\'INSCRIRE' : 'SIGN UP'}
                </button>
              </div>
            ) : null}

            {/* Language + Theme row */}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg px-4 py-3 font-medium bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15 transition-colors text-gray-800 dark:text-gray-200"
                style={{ color: 'inherit' }}
                aria-label="Toggle theme"
              >
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span className="text-sm">{theme === 'light' ? 'Dark' : 'Light'}</span>
              </button>
              {enabledLanguages.length > 1 && (
              <div
                className="flex-1 flex items-center justify-center gap-1 rounded-lg p-1"
                style={{
                  backgroundColor: theme === 'dark'
                    ? 'color-mix(in srgb, var(--nav-text, #F9FAFB) 12%, transparent)'
                    : 'color-mix(in srgb, #111827 8%, transparent)'
                }}
              >
                {enabledLanguages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => toggleLanguage(lang)}
                    className={`flex min-h-11 flex-1 items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                      language === lang ? 'shadow-sm' : 'opacity-75 hover:opacity-100'
                    }`}
                    style={language === lang
                      ? { backgroundColor: 'var(--charity-primary)', color: 'var(--charity-primary-contrast)' }
                      : { color: 'inherit' }}
                  >
                    {languageLabels[lang]}
                  </button>
                ))}
              </div>              )}
            </div>
          </div>
        </div>
      )}

      <main className="site-content">
        <Suspense fallback={<div className="min-h-[50vh] transition-colors flex items-center justify-center" style={{ backgroundColor: theme === 'dark' ? 'var(--app-bg-dark, #111827)' : 'var(--app-bg-light, #F9FAFB)' }}><Loader2 className="w-8 h-8 text-emerald-500 animate-spin" /></div>}>
        <Routes>
          <Route path="/" element={<HomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/code/:partnerCode" element={<HomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/partner/:partnerId" element={<PartnerDetailPage />} />
          <Route path="/chase-the-ace" element={<ChaseTheAceHomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/about" element={renderDefaultPage('/about', <AboutPage />)} />
          <Route path="/contact" element={renderDefaultPage('/contact', <ContactPage />)} />
          <Route path="/winners" element={renderDefaultPage('/winners', <WinnersPage />)} />
          <Route path="/draws" element={renderDefaultPage('/draws', <DrawsPage />)} />
          <Route path="/faq" element={renderDefaultPage('/faq', <FAQPage />)} />
          <Route path="/how-to-play" element={renderDefaultPage('/how-to-play', <HowToPlayPage />)} />
          <Route path="/howtoplay" element={<Navigate to="/how-to-play" replace />} />
          <Route path="/purchase" element={<DrawOpenRoute><ProtectedRoute onLoginRequired={handleOpenLogin} allowGuest><PurchasePage /></ProtectedRoute></DrawOpenRoute>} />
          <Route path="/chase-the-ace-purchase" element={<DrawOpenRoute><ProtectedRoute onLoginRequired={handleOpenLogin} allowGuest><ChaseTheAcePurchasePage /></ProtectedRoute></DrawOpenRoute>} />
          <Route path="/chase-the-ace-card" element={<DrawOpenRoute><ProtectedRoute onLoginRequired={handleOpenLogin} allowGuest><ChaseTheAceCardPage /></ProtectedRoute></DrawOpenRoute>} />
          <Route path="/superball-bingo-purchase" element={<DrawOpenRoute><ProtectedRoute onLoginRequired={handleOpenLogin} allowGuest><SuperballBingoPurchasePage /></ProtectedRoute></DrawOpenRoute>} />
          <Route path="/square-game-purchase" element={<DrawOpenRoute><ProtectedRoute onLoginRequired={handleOpenLogin} allowGuest><SquareGamePurchasePage /></ProtectedRoute></DrawOpenRoute>} />
          <Route path="/checkout" element={<DrawOpenRoute><ProtectedRoute onLoginRequired={handleOpenLogin} allowGuest><CheckoutPage /></ProtectedRoute></DrawOpenRoute>} />
          <Route path="/purchase-complete" element={<ProtectedRoute onLoginRequired={handleOpenLogin} allowGuest><PurchaseCompletePage /></ProtectedRoute>} />
          <Route path="/receipt/:id" element={<ProtectedRoute onLoginRequired={handleOpenLogin} allowGuest><ReceiptPage /></ProtectedRoute>} />
          <Route path="/superball-bingo" element={<SuperballBingoHomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/elimination-draw" element={<EliminationDrawHomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/progressive-5050" element={<Progressive5050HomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/modified-5050" element={<Modified5050HomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/square-game" element={<SquareGameHomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/prize-draw" element={<PrizeDrawHomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/terms" element={renderDefaultPage('/terms', <TermsPage />)} />
          <Route path="/privacy" element={renderDefaultPage('/privacy', <PrivacyPage />)} />
          <Route path="/responsible-gaming" element={renderDefaultPage('/responsible-gaming', <ResponsibleGamingPage />)} />
          <Route path="/account" element={<ProtectedRoute onLoginRequired={handleOpenLogin}><AccountPage /></ProtectedRoute>} />
          <Route path="/account/my-squares" element={<ProtectedRoute onLoginRequired={handleOpenLogin}><MySquaresPage /></ProtectedRoute>} />
          <Route path="/account/my-tickets" element={<Navigate to="/account?tab=tickets" replace />} />
          <Route path="/geo-blocked" element={<GeoBlockedPage />} />
          <Route path="/firewall-restricted" element={<FirewallRestrictedPage />} />
          <Route path="/display/classic5050" element={<Classic5050Display />} />
          <Route path="/display/progressive5050" element={<Progressive5050Display />} />
          <Route path="/display/modified5050" element={<Modified5050Display />} />
          <Route path="/display/chase-the-ace" element={<ChaseTheAceDisplay />} />
          <Route path="/display/superball-bingo" element={<SuperballBingoDisplay />} />
          <Route path="/display/elimination" element={<EliminationDrawDisplay />} />
          <Route path="/display/prize-draw" element={<PrizeDrawDisplay />} />
          <Route path="/display/square-game" element={<SquareGameDisplay />} />
          <Route path="/jackpot-test" element={<JackpotTestPage />} />
          <Route path="/all-partners" element={<AllPartnersPage />} />
          <Route path="/offline" element={<OfflinePage />} />
          <Route path="/closed" element={<ClosedPage />} />
          <Route path="/excluded" element={<ExcludedPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/finalize-account" element={<FinalizeAccountPage />} />
          <Route path="/account-deactivated" element={<AccountDeactivatedPage />} />
          <Route path="/renew-tickets" element={<Navigate to="/purchase" replace />} />
          <Route path="/signup" element={<HomePage onLoginClick={handleOpenLogin} />} />
          <Route path="/confirm-email" element={<RedirectWithParams to="/?confirm-email=1" />} />
          <Route path="/auth" element={<RedirectWithParams to="/?auth=1" />} />
          <Route path="/facebook-profile-notfound" element={<Navigate to="/?auth-error=facebook-profile-notfound" replace />} />
          <Route path="/google-profile-notfound" element={<Navigate to="/?auth-error=google-profile-notfound" replace />} />
          <Route path="/facebook-login-error" element={<Navigate to="/?auth-error=facebook-login-error" replace />} />
          <Route path="/google-login-error" element={<Navigate to="/?auth-error=google-login-error" replace />} />
          <Route path="/facebook-email-required" element={<Navigate to="/?auth-error=facebook-email-required" replace />} />
          <Route path="/member-exists" element={<Navigate to="/?auth-error=member-exists" replace />} />

          {/* Preview routes — mirrors of main pages with draft data forced on */}
          <Route path="/preview" element={<PreviewPage><HomePage onLoginClick={handleOpenLogin} /></PreviewPage>} />
          <Route path="/preview/chase-the-ace" element={<PreviewPage><ChaseTheAceHomePage onLoginClick={handleOpenLogin} /></PreviewPage>} />
          <Route path="/preview/superball-bingo" element={<PreviewPage><SuperballBingoHomePage onLoginClick={handleOpenLogin} /></PreviewPage>} />
          <Route path="/preview/elimination-draw" element={<PreviewPage><EliminationDrawHomePage onLoginClick={handleOpenLogin} /></PreviewPage>} />
          <Route path="/preview/progressive-5050" element={<PreviewPage><Progressive5050HomePage onLoginClick={handleOpenLogin} /></PreviewPage>} />
          <Route path="/preview/modified-5050" element={<PreviewPage><Modified5050HomePage onLoginClick={handleOpenLogin} /></PreviewPage>} />
          <Route path="/preview/square-game" element={<PreviewPage><SquareGameHomePage onLoginClick={handleOpenLogin} /></PreviewPage>} />
          <Route path="/preview/prize-draw" element={<PreviewPage><PrizeDrawHomePage onLoginClick={handleOpenLogin} /></PreviewPage>} />

          <Route path="/:customPageSlug" element={<CustomPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>

      <footer className="border-t border-white/10 mt-12 sm:mt-16 transition-colors site-footer" style={{ backgroundColor: 'var(--nav-bg, #1F2937)', color: 'var(--nav-text, #F9FAFB)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Main Footer Links Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-8">
            {/* Branding Column */}
            <div className="col-span-2 md:col-span-1 space-y-2">
              {shouldUseOrganizationLogo ? (
                <img
                  src={headerLogoUrl}
                  alt={branding?.charityName || 'Organization logo'}
                  className="h-10 sm:h-12 w-auto mb-2"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Logo className="h-10 sm:h-12 w-auto mb-2" />
              )}
              <p className="text-xs text-white/60 leading-relaxed max-w-xs">
                {siteDetails?.organizationSlogan || siteDetails?.tagline || t('footer.tagline', 'Licensed and transparent raffle and lottery fundraising for local community causes.')}
              </p>
            </div>

            {/* Quick Links Column */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider mb-3 sm:mb-4 text-white/50" style={{ color: 'var(--nav-text)' }}>
                {t('footer.quickLinks')}
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                {DEFAULT_PAGES.filter(page => page.footerSection === 'quick' && isDefaultPageEnabled(page.path, defaultPageVisibility)).map(page => (
                  <li key={page.path}>
                    <Link to={page.path} className="opacity-70 hover:opacity-100 hover:text-charity-primary transition-all flex items-center gap-1.5" style={{ color: 'inherit' }}>
                      {t(page.footerLabelKey, page.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support Column */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider mb-3 sm:mb-4 text-white/50" style={{ color: 'var(--nav-text)' }}>
                {t('footer.support')}
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                {DEFAULT_PAGES.filter(page => page.footerSection === 'support' && isDefaultPageEnabled(page.path, defaultPageVisibility)).map(page => (
                  <li key={page.path}>
                    <Link to={page.path} className="opacity-70 hover:opacity-100 hover:text-charity-primary transition-all flex items-center gap-1.5" style={{ color: 'inherit' }}>
                      {t(page.footerLabelKey, page.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Certifications Column */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider mb-3 sm:mb-4 text-white/50" style={{ color: 'var(--nav-text)' }}>
                {t('footer.certifications')}
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm opacity-80">
                <li>
                  <a href="https://tlffs.ca/certifications" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 hover:text-charity-primary transition-all inline-flex items-center gap-1" style={{ color: 'inherit' }}>
                    {t('footer.viewCertifications', 'Visit Our Website')}
                    <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {import.meta.env.DEV && (
          <div className="border-t border-white/10 pt-8 mt-8">
            <div className="text-center mb-8">
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--nav-text)' }}>
                Customize Your Experience
              </h3>
              <p className="text-sm opacity-60">
                This is a POC - all settings are editable and will be connected to your API
              </p>
            </div>

            <div className="max-w-6xl mx-auto space-y-6">
              <Suspense fallback={<div className="h-16 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
                <GameTypeDropdown />
                <LayoutStyleDropdown />
                <ColorThemeDropdown />
                <CountdownStyleDropdown />
                <JackpotStyleDropdown />
                <SuperballBingoSettings />
                <LanguageSettings />
                <AuthSettings />
                <EditModeSettings />
              </Suspense>
            </div>
          </div>
          )}

          {/* Bottom Trust & Copyright Bar */}
          <div className="border-t border-white/10 pt-6 mt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2.5">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 text-xs font-bold text-emerald-300 shadow-sm">
                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{siteDetails?.formattedLicenses ? `${t('footer.license', 'License:')} ${siteDetails.formattedLicenses}` : `${t('footer.license', 'License:')} #${siteDetails?.licenseNumber || 'RAF1366831'}`}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-bold text-white/70">
                <span className="flex h-4.5 min-w-[20px] items-center justify-center rounded-full bg-amber-500/20 px-1 text-[10px] font-black text-amber-300">{legalMinAge}</span>
                <span>{t('footer.responsibleGaming', 'Play Responsibly')}</span>
              </div>
            </div>

            <p className="text-xs text-center md:text-right opacity-60">
              © {new Date().getFullYear()} TLF Fundraising Solutions. {t('footer.allRightsReserved')}.
            </p>
          </div>
        </div>
      </footer>

      <Suspense fallback={null}>
        <LoginModal
          isOpen={loginModalOpen}
          onClose={() => setLoginModalOpen(false)}
          initialMode={loginModalMode}
          onLoginSuccess={handleLoginSuccess}
          initialError={authNotification?.type === 'error' ? authNotification.message : null}
        />

        <PasswordResetModal
          isOpen={passwordResetModalOpen}
          onClose={() => setPasswordResetModalOpen(false)}
          memberId={resetMemberId}
          token={resetCodeToken}
          onSuccess={() => {
            setLoginModalMode('login');
            setLoginModalOpen(true);
          }}
        />

        <EditModeToggle
          editMode={editMode}
          onToggleEdit={toggleEditMode}
          onSave={handleSave}
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={isSaving}
        />

        <ThemeCustomizerPanel />
      </Suspense>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  // Initialize analytics after load so GA never competes with first paint.
  useEffect(() => {
    const scheduleAnalytics = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          window.setTimeout(() => {
            initializeAnalytics();
          }, 1500);
        }, { timeout: 5000 });
        return;
      }

      window.setTimeout(() => {
        initializeAnalytics();
      }, 2500);
    };

    if (document.readyState === 'complete') {
      scheduleAnalytics();
      return undefined;
    }

    window.addEventListener('load', scheduleAnalytics, { once: true });
    return () => window.removeEventListener('load', scheduleAnalytics);
  }, []);

  // Auto-track page views and engagement
  useTlfPageTracking();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function GeoCheckingScreen() {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
      <div className="max-w-lg w-full text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-gray-400 dark:text-gray-500 animate-spin" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
          {t('geo.verifyingTitle', 'Checking Availability')}
        </h1>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
          {t('geo.verifyingMessage', 'Gaming and raffle participation is governed by local laws. Our platform is currently verifying your location to ensure compliance with regional regulations.')}
        </p>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          {t('geo.verifyingWait', 'This only takes a moment...')}
        </p>
      </div>
    </div>
  );
}

function getNavItemIcon(toPath) {
  const path = toPath?.toLowerCase() || '';
  if (path === '/') return Home;
  if (path === '/about') return Info;
  if (path === '/how-to-play') return HelpCircle;
  if (path === '/winners') return Trophy;
  if (path === '/draws') return Calendar;
  if (path === '/faq') return HelpCircle;
  return FileText;
}

function isPurchasePath(pathname) {
  const clean = (pathname || '').replace(/\/$/, '').toLowerCase();
  return [
    '/purchase',
    '/chase-the-ace-purchase',
    '/chase-the-ace-card',
    '/superball-bingo-purchase',
    '/square-game-purchase',
    '/renew-tickets'
  ].includes(clean) || clean.startsWith('/purchase/');
}

function isBillingPath(pathname) {
  const clean = (pathname || '').replace(/\/$/, '').toLowerCase();
  return clean === '/checkout' || clean.startsWith('/checkout/');
}

function shouldEnforceGeoBlock(pathname, stage = 'SiteLoad') {
  if (!stage || stage === 'SiteLoad') {
    return true; // Enforce across entire site
  }
  if (stage === 'PurchasePage') {
    return isPurchasePath(pathname) || isBillingPath(pathname);
  }
  if (stage === 'BillingPage') {
    return isBillingPath(pathname);
  }
  return true;
}

function AppRoutes() {
  const location = useLocation();
  const { isAllowed, isChecking, hasVerified, verifyLocation, geoBlockStage: contextGeoBlockStage } = useGeoLocation();
  const { loading: siteLoading, siteDetails } = useSiteConfig();
  const { draftAppearance, shouldShowDraft } = useDraft();
  const geoBlockStage = siteDetails?.geoBlockStage || contextGeoBlockStage || 'SiteLoad';

  // If navigating into an enforced route and location check has not completed yet, trigger verification
  useEffect(() => {
    if (shouldEnforceGeoBlock(location.pathname, geoBlockStage) && !hasVerified && !isChecking && typeof verifyLocation === 'function') {
      verifyLocation();
    }
  }, [location.pathname, geoBlockStage, hasVerified, isChecking, verifyLocation]);

  // Capture partner attribution from the URL on every navigation, independent of
  // the `isPartnerDropdown` site flag. Supports ?partnerId/?partner/?code/?partnerCode
  // query params plus the /code/:code and /partner/:idOrCode landing paths. A no-op
  // when the URL carries no partner reference, so it never clears an existing selection.
  useEffect(() => {
    capturePartnerSelectionFromLocation(location);
  }, [location.pathname, location.search]);

  const { isAdmin, loading: isAdminLoading } = useIsAdmin();
  const isErrorPage = location.pathname === '/geo-blocked' || location.pathname === '/firewall-restricted';
  const isStandalonePage = [
    '/offline', '/closed', '/coming-soon', '/excluded', 
    '/unsubscribe', '/finalize-account', '/account-deactivated'
  ].includes(location.pathname.replace(/\/$/, ''));
  const isDisplayPage = location.pathname.startsWith('/display/') || location.pathname === '/display';
  const defaultPageVisibility = getDefaultPageVisibility(
    draftAppearance?.defaultPageVisibility
      ?? draftAppearance?.default_page_visibility
      ?? window.SITE_CONFIG?.defaults?.default_page_visibility
      ?? window.SITE_CONFIG?.defaults?.defaultPageVisibility
  );

  // Admin pages render without site navigation
  if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
    return (
      <Routes>
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/pages" element={<AdminPagesPage />} />
      </Routes>
    );
  }

  // Standalone status/utility pages render without site navigation
  if (isStandalonePage) {
    return (
      <Routes>
        <Route path="/offline" element={<OfflinePage />} />
        <Route path="/closed" element={<ClosedPage />} />
        <Route path="/coming-soon" element={<ComingSoonPage />} />
        <Route path="/excluded" element={<ExcludedPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/finalize-account" element={<FinalizeAccountPage />} />
        <Route path="/account-deactivated" element={<AccountDeactivatedPage />} />
      </Routes>
    );
  }

  if (isErrorPage) {
    const searchParams = new URLSearchParams(location.search);
    const isPreview = searchParams.get('preview') === '1';
    if (location.pathname === '/geo-blocked' && !isChecking && isAllowed && !isPreview) {
      return <Navigate to="/" replace />;
    }
    return (
      <Routes>
        <Route path="/geo-blocked" element={<GeoBlockedPage />} />
        <Route path="/firewall-restricted" element={<FirewallRestrictedPage />} />
      </Routes>
    );
  }

  // Geo-blocking: redirect when confirmed blocked on an enforced route. Don't block initial render
  // while checking — allows LCP content to paint immediately. If the user turns
  // out to be blocked, the redirect fires once the check completes (~300ms).
  if (!isChecking && !isAllowed && shouldEnforceGeoBlock(location.pathname, geoBlockStage)) {
    return <Navigate to="/geo-blocked" replace />;
  }

  // Public visibility, Offline toggle, and Terminal Sales Only gating — check after geo/firewall but before rendering site
  if (!siteLoading && (siteDetails?.isOffline === true || siteDetails?.hasFrontEnd === false)) {
    return <Navigate to="/offline" replace />;
  }

  const isPrepDomain = siteDetails?.isPreparationDomain === true || window.SITE_CONFIG?.runtime?.is_preparation_domain === true;
  const isComingSoon = siteDetails?.isComingSoon === true || siteDetails?.publicVisibility === 'ComingSoon' || siteDetails?.publicVisibility === 0;

  if (!siteLoading && !isPrepDomain && isComingSoon) {
    return <Navigate to="/coming-soon" replace />;
  }

  if (!siteLoading && (siteDetails?.publicVisibility === 'Hidden' || siteDetails?.publicVisibility === 2)) {
    return <Navigate to="/offline" replace />;
  }

  const isJackpotTest = location.pathname === '/jackpot-test';

  if (isDisplayPage || isJackpotTest) {
    return (
      <Routes>
        <Route path="/display" element={<FeedDisplay />} />
        <Route path="/display/classic5050" element={<Classic5050Display />} />
        <Route path="/display/progressive5050" element={<Progressive5050Display />} />
        <Route path="/display/modified5050" element={<Modified5050Display />} />
        <Route path="/display/chase-the-ace" element={<ChaseTheAceDisplay />} />
        <Route path="/display/superball-bingo" element={<SuperballBingoDisplay />} />
        <Route path="/display/elimination" element={<EliminationDrawDisplay />} />
        <Route path="/display/prize-draw" element={<PrizeDrawDisplay />} />
        <Route path="/display/square-game" element={<SquareGameDisplay />} />
        <Route path="/display/:lang" element={<FeedDisplay />} />
        <Route path="/jackpot-test" element={<JackpotTestPage />} />
      </Routes>
    );
  }

  return <Navigation />;
}

function App() {
  return (
    <Router unstable_useTransitions={false}>
      <SignalRProvider>
        <ToastProvider>
          <ScrollToTop />
          <AppRoutes />
        </ToastProvider>
      </SignalRProvider>
    </Router>
  );
}

export default App;
